import { FC, useState } from 'react';
import styled from 'styled-components';
import { useEditorData } from '../../lib/FabricJs';
import {
  AlignEdge,
  Axis,
  getAlignOffset,
  getDistributeOffsets,
  getUnionBounds,
  Offset,
} from '../../utils/alignment';
import { getSlideBounds } from '../../utils/objectGeometry';
import { editorTheme } from '../../theme';
import Section, { ButtonGrid, PanelButton } from './Section';
import { useSelectionEdit } from './useSelectionEdit';

const Segmented = styled.div`
  display: flex;
  border: 1px solid ${editorTheme.border};
  border-radius: 6px;
  overflow: hidden;
`;

const SegmentButton = styled.button<{ selected?: boolean }>`
  flex: 1;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0;
  font-size: 12px;
  color: ${({ selected }) =>
    selected ? editorTheme.accentHover : editorTheme.text};
  background: ${({ selected }) =>
    selected ? editorTheme.accentSoft : editorTheme.surface};
  & + & {
    border-left: 1px solid ${editorTheme.border};
  }
  &:hover {
    color: ${editorTheme.accentHover};
  }
`;

const iconProps = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  'aria-hidden': true,
};

const ALIGN_BUTTONS: { edge: AlignEdge; label: string; icon: JSX.Element }[] = [
  {
    edge: 'left',
    label: 'Align left',
    icon: (
      <svg {...iconProps}>
        <path d="M4 3v18" />
        <rect x="8" y="6" width="10" height="4" rx="1" />
        <rect x="8" y="14" width="6" height="4" rx="1" />
      </svg>
    ),
  },
  {
    edge: 'center',
    label: 'Align center',
    icon: (
      <svg {...iconProps}>
        <path d="M12 3v18" />
        <rect x="6" y="6" width="12" height="4" rx="1" />
        <rect x="8" y="14" width="8" height="4" rx="1" />
      </svg>
    ),
  },
  {
    edge: 'right',
    label: 'Align right',
    icon: (
      <svg {...iconProps}>
        <path d="M20 3v18" />
        <rect x="6" y="6" width="10" height="4" rx="1" />
        <rect x="10" y="14" width="6" height="4" rx="1" />
      </svg>
    ),
  },
  {
    edge: 'top',
    label: 'Align top',
    icon: (
      <svg {...iconProps}>
        <path d="M3 4h18" />
        <rect x="6" y="8" width="4" height="10" rx="1" />
        <rect x="14" y="8" width="4" height="6" rx="1" />
      </svg>
    ),
  },
  {
    edge: 'middle',
    label: 'Align middle',
    icon: (
      <svg {...iconProps}>
        <path d="M3 12h18" />
        <rect x="6" y="6" width="4" height="12" rx="1" />
        <rect x="14" y="8" width="4" height="8" rx="1" />
      </svg>
    ),
  },
  {
    edge: 'bottom',
    label: 'Align bottom',
    icon: (
      <svg {...iconProps}>
        <path d="M3 20h18" />
        <rect x="6" y="6" width="4" height="10" rx="1" />
        <rect x="14" y="10" width="4" height="6" rx="1" />
      </svg>
    ),
  },
];

const moveBy = (object: fabric.Object, { x, y }: Offset) => {
  if (object.lockMovementX && object.lockMovementY) return;
  object.set({
    left: (object.left || 0) + (object.lockMovementX ? 0 : x),
    top: (object.top || 0) + (object.lockMovementY ? 0 : y),
  });
  object.setCoords();
};

enum AlignTarget {
  Selection = 'Selection',
  Slide = 'Slide',
}

const AlignDistribute: FC = () => {
  const { canvas } = useEditorData();
  const editInSlideCoordinates = useSelectionEdit();
  const [alignTarget, setAlignTarget] = useState(AlignTarget.Selection);

  const activeObject = canvas?.getActiveObject();
  if (!canvas || !activeObject) return null;
  const objects = canvas.getActiveObjects();
  const isMultiple = objects.length > 1;

  const zoom = canvas.getZoom() || 1;
  const slideBounds = {
    left: 0,
    top: 0,
    width: canvas.getWidth() / zoom,
    height: canvas.getHeight() / zoom,
  };

  const align = (edge: AlignEdge) =>
    editInSlideCoordinates((targets) => {
      const boundsList = targets.map(getSlideBounds);
      const target =
        isMultiple && alignTarget === AlignTarget.Selection
          ? getUnionBounds(boundsList)
          : slideBounds;
      targets.forEach((object, index) =>
        moveBy(object, getAlignOffset(boundsList[index], target, edge)),
      );
    });

  const distribute = (axis: Axis) =>
    editInSlideCoordinates((targets) => {
      const offsets = getDistributeOffsets(targets.map(getSlideBounds), axis);
      targets.forEach((object, index) => moveBy(object, offsets[index]));
    });

  return (
    <>
      <Section title={isMultiple ? 'Align' : 'Align to slide'}>
        {isMultiple && (
          <Segmented role="radiogroup" aria-label="Align to">
            {Object.values(AlignTarget).map((value) => (
              <SegmentButton
                key={value}
                role="radio"
                aria-checked={alignTarget === value}
                selected={alignTarget === value}
                onClick={() => setAlignTarget(value)}
              >
                To {value.toLowerCase()}
              </SegmentButton>
            ))}
          </Segmented>
        )}
        <Segmented>
          {ALIGN_BUTTONS.map(({ edge, label, icon }) => (
            <SegmentButton
              key={edge}
              aria-label={label}
              title={label}
              onClick={() => align(edge)}
            >
              {icon}
            </SegmentButton>
          ))}
        </Segmented>
      </Section>
      {objects.length > 2 && (
        <Section title="Distribute">
          <ButtonGrid columns={2}>
            <PanelButton onClick={() => distribute('horizontal')}>
              Horizontally
            </PanelButton>
            <PanelButton onClick={() => distribute('vertical')}>
              Vertically
            </PanelButton>
          </ButtonGrid>
        </Section>
      )}
    </>
  );
};

export default AlignDistribute;
